import { useMemo } from "react";
import { motion } from "framer-motion";

interface SplitTextRevealProps {
  children: string;
  as?: "h1" | "h2" | "h3" | "h4" | "p" | "span" | "div";
  splitType?: "chars" | "words" | "lines";
  stagger?: number;
  duration?: number;
  className?: string;
}

const MotionTag = {
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  h4: motion.h4,
  p: motion.p,
  span: motion.span,
  div: motion.div,
} as const;

export function SplitTextReveal({
  children,
  as: tag = "p",
  splitType = "chars",
  stagger = 0.03,
  duration = 0.6,
  className,
}: SplitTextRevealProps) {
  const Tag = MotionTag[tag];

  const pieces = useMemo(() => {
    if (splitType === "chars") {
      return children.split("").map((char, i) => ({
        key: `${i}-${char}`,
        text: char === " " ? "\u00A0" : char,
      }));
    }
    return children
      .split(/\s+/)
      .filter(Boolean)
      .map((word, i) => ({
        key: `${i}-${word}`,
        text: word,
      }));
  }, [children, splitType]);

  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "0px 0px -15% 0px" }}
      transition={{ staggerChildren: stagger }}
    >
      {pieces.map(({ key, text }) => (
        <motion.span
          key={key}
          className="inline-block"
          variants={{
            hidden: { y: 50, opacity: 0 },
            visible: { y: 0, opacity: 1 },
          }}
          transition={{ duration, ease: [0.33, 0, 0, 1] }}
        >
          {text}
          {splitType !== "chars" && "\u00A0"}
        </motion.span>
      ))}
    </Tag>
  );
}
